import { TextButtonTW } from '../../../../../tailwindComponents/buttonTW/TextButtonTW';
import { Show } from '../../../../shared/Show';
import { SpinnerTW } from '../../../../../tailwindComponents/spinnerTW/SpinnerTW';
import { SpinnerSize } from '../../../../../ui-toolkit';
import React, { useCallback } from 'react';
import { checkForMutationSuccess } from '../../../../../API/checkForMutationSuccess';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../../types/shared/UUID';
import { IDocumentVersionResponse } from '../../../../../types/order/documents/shared/IDocumentVersionResponse';
import { SpruceMutationTrigger } from '../../../../../types/SpruceMutationTrigger';
import { ePermissions } from '../../../../../types/administration/userAdministration/ePermissions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { useListAdminStandardDocumentsQuery } from '../../../../../API/order/documents/DocumentApi';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';

export const WARRANTY_DEED_CATEGORY = 'deed-warranty-deed-unexecuted';

interface SubmissionParams {
  data: {
    orderId: string;
    category: string;
  };
}

export const GenerateDeedButton: React.FC<{
  isDocumentGenerating: boolean;
  generateDocumentMutation: SpruceMutationTrigger<SubmissionParams, IDocumentVersionResponse>;
}> = ({ isDocumentGenerating, generateDocumentMutation }) => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const { hasDocument } = useListAdminStandardDocumentsQuery(
    { orderId, categories: [WARRANTY_DEED_CATEGORY] },
    {
      selectFromResult: ({ data = [] }) => ({
        hasDocument: data.length > 0,
      }),
    }
  );
  const canEditTransferDeed = useHasPermissions([ePermissions.TransferDeedEdit]);

  const onGenerateDeedClick = useCallback(async () => {
    const result = await generateDocumentMutation({
      data: { orderId, category: WARRANTY_DEED_CATEGORY },
    });
    checkForMutationSuccess(result, 'generating deed');
  }, [generateDocumentMutation, orderId]);

  return (
    <section className={'flex items-center gap-2'}>
      <Show
        when={canEditTransferDeed}
        fallback={
          <div className={'text-red-600'} data-test-id={'no-permissions-transfer-deed'}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            No permission to edit transfer deed. Please consult with Clearance Team if you need to make changes to a
            prepared deed.
          </div>
        }
      >
        <TextButtonTW
          disabled={isDocumentGenerating}
          className="w-auto cursor-pointer text-blue-600"
          onClick={onGenerateDeedClick}
        >
          {hasDocument ? 'Regenerate Deed' : 'Generate Deed'}
        </TextButtonTW>
        <Show when={isDocumentGenerating}>
          <SpinnerTW className={'pt-3'} size={SpinnerSize.small} />
        </Show>
      </Show>
    </section>
  );
};
